import { TabContext, TabList } from "@mui/lab";
import Tab from "@mui/material/Tab";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useLang from "Shared/hooks/useLanguage";
import BreadCrumbs from "Utils/Common/Breadcrumb";
import PathologyReportSelect from "./PathologyReportSelect";
import {
  CASE_ID,
  CASE_NAME,
  createDefaultResultPart,
  DEFAULT_IHC_COMMENT,
  INITIAL_CASSETTES,
  MAIN_TABS,
  reportTypeOptions,
  type IhcPanelKey,
  type MainTabKey,
  type ResultPart,
  type SelectOption,
} from "./pathologyReportData";
import AddendaTab from "./tabs/AddendaTab";
import BlockManagementTab from "./tabs/BlockManagementTab";
import IhcAncillaryTab from "./tabs/IhcAncillaryTab";
import ImagesTab from "./tabs/ImagesTab";
import ResultTab from "./tabs/ResultTab";
import SignOutTab from "./tabs/SignOutTab";
import SynopticTab from "./tabs/SynopticTab";

const PathologyReport = () => {
  const { t } = useLang();
  const navigate = useNavigate();

  const [mainTab, setMainTab] = useState<MainTabKey>("result");
  const [reportType, setReportType] = useState<SelectOption | null>(null);

  const initialPart = useMemo(() => createDefaultResultPart(1, true), []);
  const [resultParts, setResultParts] = useState<ResultPart[]>([initialPart]);
  const [activeResultPartId, setActiveResultPartId] = useState(initialPart.id);

  const [synopticTumorSite, setSynopticTumorSite] = useState<SelectOption | null>(
    null
  );
  const [activeIhcPanels, setActiveIhcPanels] = useState<IhcPanelKey[]>(["mmr"]);
  const [ihcMarkerValues, setIhcMarkerValues] = useState<
    Record<string, SelectOption | null>
  >({});
  const [ihcComment, setIhcComment] = useState(DEFAULT_IHC_COMMENT);

  const [signingPathologist, setSigningPathologist] =
    useState<SelectOption | null>(null);
  const [reportStatus, setReportStatus] = useState<SelectOption | null>(null);
  const [additionalPhysicians, setAdditionalPhysicians] = useState("");
  const [hasAddendaEntries, setHasAddendaEntries] = useState(false);

  const handleAddResultPart = useCallback(() => {
    const nextIndex = resultParts.length + 1;
    const part = createDefaultResultPart(nextIndex, false);
    setResultParts((prev) => [...prev, part]);
    setActiveResultPartId(part.id);
  }, [resultParts.length]);

  const handleUpdateResultPart = useCallback(
    (id: string, patch: Partial<ResultPart>) => {
      setResultParts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...patch } : p))
      );
    },
    []
  );

  const toggleIhcPanel = useCallback((key: IhcPanelKey) => {
    setActiveIhcPanels((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const handleMarkerChange = useCallback(
    (fieldId: string, value: SelectOption | null) => {
      setIhcMarkerValues((prev) => ({ ...prev, [fieldId]: value }));
    },
    []
  );

  const tabContent = useMemo(() => {
    switch (mainTab) {
      case "result":
        return (
          <ResultTab
            parts={resultParts}
            activePartId={activeResultPartId}
            onActivePartChange={setActiveResultPartId}
            onAddPart={handleAddResultPart}
            onUpdatePart={handleUpdateResultPart}
          />
        );
      case "synoptic":
        return (
          <SynopticTab
            tumorSite={synopticTumorSite}
            onTumorSiteChange={setSynopticTumorSite}
          />
        );
      case "ihc":
        return (
          <IhcAncillaryTab
            activePanels={activeIhcPanels}
            markerValues={ihcMarkerValues}
            ihcComment={ihcComment}
            onTogglePanel={toggleIhcPanel}
            onMarkerChange={handleMarkerChange}
            onCommentChange={setIhcComment}
          />
        );
      case "images":
        return <ImagesTab />;
      case "block":
        return <BlockManagementTab cassettes={INITIAL_CASSETTES} />;
      case "addenda":
        return (
          <AddendaTab onHasAddendaChange={setHasAddendaEntries} />
        );
      case "signout":
        return (
          <SignOutTab
            signingPathologist={signingPathologist}
            reportStatus={reportStatus}
            additionalPhysicians={additionalPhysicians}
            onSigningPathologistChange={setSigningPathologist}
            onReportStatusChange={setReportStatus}
            onAdditionalPhysiciansChange={setAdditionalPhysicians}
          />
        );
      default:
        return null;
    }
  }, [
    mainTab,
    resultParts,
    activeResultPartId,
    handleAddResultPart,
    handleUpdateResultPart,
    synopticTumorSite,
    activeIhcPanels,
    ihcMarkerValues,
    ihcComment,
    toggleIhcPanel,
    handleMarkerChange,
    signingPathologist,
    reportStatus,
    additionalPhysicians,
  ]);

  return (
    <>
      <style>{`
        .pathologyReportSubTabs .nav-link.active,
        .pathologyReportSubTabs .nav-link.active:hover,
        .pathologyReportSubTabs .nav-link.active:focus {
          border-bottom: 2px solid #69A54B !important;
          color: #69A54B !important;
        }
        .pathologyReportMainTabs .MuiTab-root.Mui-selected {
          color: #69A54B !important;
        }
        .pathology-ihc-her2-active {
          background-color: #7c3aed !important;
          border-color: #7c3aed !important;
          color: #fff !important;
        }
        /* .form-control sets a fixed height; rows/min-height are ignored without this */
        textarea.pathology-report-textarea.form-control {
          height: auto !important;
          min-height: 150px;
        }
        textarea.pathology-report-textarea-sm.form-control {
          height: auto !important;
          min-height: 100px;
        }
        .pathology-report-cancel-btn {
          background-color: #e4e6ef !important;
          border-color: #d1d5db !important;
          color: #3f4254 !important;
        }
        .pathology-report-cancel-btn:hover {
          background-color: #d8dbe4 !important;
          border-color: #c4c8d0 !important;
          color: #3f4254 !important;
        }
        .pathology-case-id-badge {
          background-color: #e9ebf0 !important;
          color: #212529 !important;
          font-weight: 600;
        }
        .pathology-case-name {
          color: #7e8299 !important;
        }
      `}</style>

      <div className="app-toolbar py-2 py-lg-3">
        <div className="app-container container-fluid d-flex flex-wrap gap-3 justify-content-between align-items-center">
          <BreadCrumbs />
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-sm btn-light fw-semibold px-5 pathology-report-cancel-btn"
              onClick={() => navigate(-1)}
            >
              {t("cancel")}
            </button>
            {!(mainTab === "addenda" && hasAddendaEntries) && (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-primary fw-semibold px-5"
                >
                  {t("Save")}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-warning fw-semibold px-5"
                >
                  {t("Publish & validate")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="app-container container-fluid pb-6">
        <div className="card shadow-sm mb-4">
          <div className="card-body py-3 px-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-3">
                <span className="badge rounded-pill px-4 py-2 fs-7 pathology-case-id-badge">
                  {CASE_ID}
                </span>
                <span className="fw-semibold pathology-case-name">{CASE_NAME}</span>
              </div>
              <div style={{ minWidth: 280, maxWidth: 360 }} className="w-100">
                <PathologyReportSelect
                  value={reportType}
                  onChange={setReportType}
                  options={reportTypeOptions}
                />
              </div>
            </div>
          </div>
        </div>

        <TabContext value={mainTab}>
          <TabList
            onChange={(_, v) => setMainTab(v as MainTabKey)}
            aria-label="Pathology report tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            className="pathologyReportMainTabs"
            sx={{
              minHeight: "auto !important",
              "& .MuiButtonBase-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9rem",
              },
              "& .Mui-selected": {
                background: "#fff",
                borderStartStartRadius: "8px",
                borderStartEndRadius: "8px",
                zIndex: 4,
                fontWeight: 700,
              },
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            {MAIN_TABS.map(({ key, label }) => (
              <Tab key={key} label={t(label)} value={key} />
            ))}
          </TabList>

          <div className="card shadow-sm rounded-top-0">
            <div className="card-body p-4 p-lg-5">{tabContent}</div>
          </div>
        </TabContext>
      </div>
    </>
  );
};

export default PathologyReport;
